'use client'

import Link from 'next/link'
import { MapPin } from 'lucide-react'
import React, { useCallback, useEffect, useId, useMemo, useState } from 'react'

import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselDots,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel'
import { localizePath } from '@/lib/i18n/config'
import { useLocale } from '@/lib/i18n/useLocale'
import { useMessages } from '@/lib/i18n/useMessages'
import { getInspectionCalendarHref } from '@/lib/pages/inspectionCalendarLink'
import { getLocationPageCopy, splitLocationDescription } from '@/lib/locations/getLocationPageCopy'
import type { City } from '@/lib/mock/cities'
import { getCityBySlug } from '@/lib/mock/cities'
import { isCityId } from '@/lib/mock/mapLocations'
import {
  getDefaultLocationSlugForCity,
  getLocationCampusesForCity,
  getLocationPageBySlug,
  type LocationPage,
  type LocationPropertyChooser,
  type LocationPropertyChooserSlide,
} from '@/lib/mock/locationPages'
import { cn } from '@/utilities/ui'

type LocationPropertyChooserSectionProps =
  | {
      city: City
      locationGallerySlides?: Record<string, LocationPropertyChooserSlide[]>
      location?: never
    }
  | {
      city?: never
      location: LocationPage
      locationGallerySlides?: never
    }

const propertyChooserArrowClassName =
  'size-10 rounded-full border-[1.5px] border-white/95 bg-black/50 text-white hover:bg-black/60 hover:text-white disabled:opacity-40'

const DEFAULT_PROPERTY_CHOOSER: LocationPropertyChooser = {
  eyebrow: 'Choose your Neon Wood property',
  title: 'Where do you want to live in {city}?',
  tabListAriaLabel: 'Neon Wood properties',
  carouselAriaLabel: 'Photos of the selected Neon Wood property',
  previousLabel: 'Previous photo',
  nextLabel: 'Next photo',
  locations: [],
  slides: {},
}

function resolveSlides(
  campusSlug: string,
  campusPage: LocationPage | undefined,
  locationGallerySlides?: LocationPropertyChooserSlide[],
  configuredSlides?: LocationPropertyChooserSlide[],
): LocationPropertyChooserSlide[] {
  if (locationGallerySlides?.length) {
    return locationGallerySlides
  }

  if (configuredSlides?.length) {
    return configuredSlides
  }

  if (!campusPage) {
    return []
  }

  const slides: LocationPropertyChooserSlide[] = [
    {
      src: campusPage.previewImage,
      alt: campusPage.previewImageAlt,
      width: 1200,
      height: 900,
    },
  ]

  for (const item of campusPage.polaroidGallery?.items?.slice(0, 2) ?? []) {
    slides.push({
      src: item.image,
      alt: item.imageAlt,
      width: 1200,
      height: 900,
    })
  }

  return slides
}

function PropertyTab({
  controls,
  id,
  isActive,
  label,
  onSelect,
}: {
  controls: string
  id: string
  isActive: boolean
  label: string
  onSelect: () => void
}) {
  return (
    <button
      aria-controls={controls}
      aria-selected={isActive}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-4 py-2.5 font-outfit text-sm font-medium whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-turquoise/50 focus-visible:ring-offset-2 focus-visible:ring-offset-cream',
        isActive
          ? 'bg-turquoise text-dark-800'
          : 'bg-[#8DC8FF] text-dark-800/80 hover:bg-[#7ab8ef]',
      )}
      id={id}
      onClick={onSelect}
      role="tab"
      type="button"
    >
      <MapPin aria-hidden className="size-4 shrink-0" />
      {label}
    </button>
  )
}

export function LocationPropertyChooserSection(
  props: LocationPropertyChooserSectionProps,
) {
  const locale = useLocale()
  const citySlug = props.city
    ? isCityId(props.city.slug)
      ? props.city.slug
      : undefined
    : props.location.citySlug
  const propertyChooser =
    props.city?.propertyChooser ?? props.location?.propertyChooser ?? DEFAULT_PROPERTY_CHOOSER
  const tabPanelId = useId()
  const headingId = useId()

  const campuses = useMemo(
    () => (citySlug ? getLocationCampusesForCity(citySlug) : []),
    [citySlug],
  )

  const tabLocations = useMemo(() => {
    if (!propertyChooser) {
      return []
    }

    const configured = propertyChooser.locations.filter((entry) =>
      campuses.some((campus) => campus.slug === entry.slug),
    )

    if (configured.length > 0) {
      return configured
    }

    return campuses.map((campus) => ({
      slug: campus.slug,
      tabLabel: campus.name.replace(/^Berlin\s+/i, '').replace(/^Tannhaus Berlin\s+/i, ''),
    }))
  }, [campuses, propertyChooser])

  const preferredCampusSlug =
    props.location?.slug ??
    (citySlug ? getDefaultLocationSlugForCity(citySlug) : undefined) ??
    tabLocations[0]?.slug ??
    ''

  const initialCampusSlug = tabLocations.some((entry) => entry.slug === preferredCampusSlug)
    ? preferredCampusSlug
    : (tabLocations[0]?.slug ?? preferredCampusSlug)

  const [selectedCampusSlug, setSelectedCampusSlug] = useState(initialCampusSlug)
  const [carouselApi, setCarouselApi] = useState<CarouselApi>()

  useEffect(() => {
    setSelectedCampusSlug(initialCampusSlug)
  }, [initialCampusSlug])

  const selectedCampusPage = useMemo(
    () => (citySlug ? getLocationPageBySlug(citySlug, selectedCampusSlug) : undefined),
    [citySlug, selectedCampusSlug],
  )

  const slides = useMemo(
    () =>
      resolveSlides(
        selectedCampusSlug,
        selectedCampusPage,
        props.locationGallerySlides?.[selectedCampusSlug],
        propertyChooser?.slides[selectedCampusSlug],
      ),
    [
      propertyChooser?.slides,
      props.locationGallerySlides,
      selectedCampusPage,
      selectedCampusSlug,
    ],
  )

  useEffect(() => {
    carouselApi?.scrollTo(0, true)
  }, [carouselApi, selectedCampusSlug])

  const messages = useMessages()
  const cityName =
    props.city?.name ??
    (citySlug && isCityId(citySlug)
      ? messages.landing.locationsMap.cities[citySlug]
      : citySlug
        ? (getCityBySlug(citySlug)?.name ?? citySlug)
        : '')

  const sectionTitle = (
    tabLocations.length > 1
      ? messages.cityPage.propertyChooserTitleMultiple
      : messages.cityPage.propertyChooserTitleSingle
  ).replace('{city}', cityName)
  const eyebrow = messages.cityPage.propertyChooserEyebrow
  const primaryCtaLabel = messages.cityPage.primaryCta
  const secondaryCtaLabel = messages.cityPage.secondaryCta
  const primaryCtaHref = getInspectionCalendarHref()
  const secondaryCtaHref = localizePath('/apartments', locale)
  const campusDescription =
    getLocationPageCopy(selectedCampusSlug, locale)?.description ??
    selectedCampusPage?.description

  const handleCampusSelect = useCallback((slug: string) => {
    setSelectedCampusSlug(slug)
  }, [])

  if (!citySlug || !propertyChooser || tabLocations.length === 0 || !selectedCampusPage) {
    return null
  }

  const carouselKey = selectedCampusSlug

  const carouselMarkup =
    slides.length > 0 ? (
      <Carousel
        key={carouselKey}
        aria-label={propertyChooser.carouselAriaLabel}
        className="relative w-full"
        opts={{ align: 'start', loop: slides.length > 1 }}
        setApi={setCarouselApi}
      >
        <div className="relative overflow-hidden rounded-[1.25rem]">
          <CarouselContent className="ml-0">
            {slides.map((slide, index) => (
              <CarouselItem
                key={`${carouselKey}-${index}`}
                className="basis-full pl-0"
              >
                <img
                  alt={slide.alt}
                  className="aspect-square w-full object-cover object-center"
                  height={slide.height ?? 900}
                  src={slide.src}
                  width={slide.width ?? 1200}
                />
              </CarouselItem>
            ))}
          </CarouselContent>

          {slides.length > 1 ? (
            <>
              <div className="absolute inset-x-0 bottom-0 z-10 flex justify-center px-5 pb-4">
                <div className="flex items-center gap-3">
                  <CarouselPrevious
                    aria-label={propertyChooser.previousLabel}
                    className={cn(
                      propertyChooserArrowClassName,
                      'static top-auto left-auto hidden translate-none lg:inline-flex',
                    )}
                    size="icon"
                    variant="outline"
                  />
                  <CarouselDots
                    getSlideLabel={(index) =>
                      messages.common.goToSlide.replace('{n}', String(index + 1))
                    }
                  />
                  <CarouselNext
                    aria-label={propertyChooser.nextLabel}
                    className={cn(
                      propertyChooserArrowClassName,
                      'static top-auto right-auto hidden translate-none lg:inline-flex',
                    )}
                    size="icon"
                    variant="outline"
                  />
                </div>
              </div>
              <CarouselPrevious
                aria-label={propertyChooser.previousLabel}
                className={cn(
                  propertyChooserArrowClassName,
                  'absolute top-1/2 left-2 z-10 -translate-y-1/2 lg:hidden',
                )}
                size="icon"
                variant="outline"
              />
              <CarouselNext
                aria-label={propertyChooser.nextLabel}
                className={cn(
                  propertyChooserArrowClassName,
                  'absolute top-1/2 right-2 z-10 -translate-y-1/2 lg:hidden',
                )}
                size="icon"
                variant="outline"
              />
            </>
          ) : null}
        </div>
      </Carousel>
    ) : null

  return (
    <section
      aria-labelledby={headingId}
      className="overflow-hidden bg-cream lg:-mt-20"
    >
      <div className="grid items-start gap-10 lg:grid-cols-2 lg:gap-x-10 xl:gap-x-6 lg:pt-24 lg:pt-0">
        <div className="px-4 pb-0 lg:pb-16 lg:pl-[max(1rem,calc((100vw-105rem)/2+1rem))] pt-8">
          <div className="flex flex-col gap-10">
            <div className="z-10 flex flex-col gap-3">
              <p className="font-outfit text-base font-medium text-dark-800/80">
                {eyebrow}
              </p>

              <h2
                className="font-display text-[2.5rem] font-semibold leading-tight tracking-[-0.04em] text-bluetext md:text-[3.5rem] lg:text-[4rem]"
                id={headingId}
              >
                {sectionTitle}
              </h2>
            </div>

            <div
              aria-label={propertyChooser.tabListAriaLabel}
              className="flex flex-wrap gap-2"
              role="tablist"
            >
              {tabLocations.map((tabLocation) => {
                const tabId = `${tabPanelId}-tab-${tabLocation.slug}`
                const isActive = selectedCampusSlug === tabLocation.slug

                return (
                  <PropertyTab
                    key={tabLocation.slug}
                    controls={tabPanelId}
                    id={tabId}
                    isActive={isActive}
                    label={tabLocation.tabLabel}
                    onSelect={() => handleCampusSelect(tabLocation.slug)}
                  />
                )
              })}
            </div>

            <div
              aria-labelledby={`${tabPanelId}-tab-${selectedCampusSlug}`}
              className="flex flex-col gap-4 lg:gap-10"
              id={tabPanelId}
              role="tabpanel"
            >
              <div className="flex items-start gap-2">
                <MapPin
                  aria-hidden
                  className="mt-0.5 size-5 shrink-0 text-dark-green"
                />
                <p className="font-outfit text-base font-semibold text-dark-800">
                  {selectedCampusPage.address}
                </p>
              </div>

              <div className="flex flex-col gap-4">
                {splitLocationDescription(campusDescription ?? '').map((paragraph) => (
                  <p
                    className="font-outfit text-base leading-relaxed text-dark-800/80"
                    key={paragraph}
                  >
                    {paragraph}
                  </p>
                ))}
              </div>

              <div className="flex items-center gap-4">
                <Link
                  className="inline-flex h-12 items-center justify-center rounded-[0.625rem] border-[1.5px] border-black/10 bg-turquoise px-4 font-outfit text-base font-medium text-dark-800/80 transition-colors hover:bg-turquoise/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-turquoise/50 focus-visible:ring-offset-2 focus-visible:ring-offset-cream"
                  href={primaryCtaHref}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {primaryCtaLabel}
                  <span className="sr-only"> {messages.common.opensInNewTab}</span>
                </Link>
                <Link
                  className="inline-flex h-12 items-center justify-center rounded-[0.625rem] border-[1.5px] border-black/30 bg-transparent px-4 font-outfit text-base font-medium text-dark-800/80 transition-colors hover:bg-white/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-turquoise/50 focus-visible:ring-offset-2 focus-visible:ring-offset-cream"
                  href={secondaryCtaHref}
                >
                  {secondaryCtaLabel}
                </Link>
              </div>
            </div>
          </div>
        </div>

        {carouselMarkup ? (
          <div className="relative flex w-full p-2 lg:col-start-2 lg:pl-36 ">
            {carouselMarkup}
          </div>
        ) : null}
      </div>
    </section>
  )
}
